import { 
  createUserWithEmailAndPassword, 
  deleteUser, 
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  updateDoc, 
  deleteField, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { auth, db } from '../firebase';

// Create a new staff member
export const createStaffMember = async (staffData) => {
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      staffData.email,
      staffData.password
    );
    
    const user = userCredential.user;
    
    // Update user profile with display name
    await updateProfile(user, {
      displayName: email.split('@')[0]
    });
    
    // Create user document in Firestore
    const userDoc = {
      uid: user.uid,
      email: staffData.email,
      staffId: staffData.staffId,
      role: staffData.role || 'staff',
      isActive: true,
      createdAt: new Date().toISOString(),
      companyId: staffData.companyId // Use the passed companyId
    };
    
    await setDoc(doc(db, 'users', user.uid), userDoc);
    
    // Send password reset email
    await sendPasswordResetEmail(auth, staffData.email);
    
    return { success: true, userId: user.uid };
  } catch (error) {
    console.error('Error creating staff member:', error);
    throw error;
  }
};

// Update staff member details
export const updateStaffMember = async (userId, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating staff member:', error);
    throw error;
  }
};

// Disable staff member (soft delete)
export const disableStaffMember = async (userId) => {
  return updateStaffMember(userId, { isActive: false });
};

// Enable staff member
export const enableStaffMember = async (userId) => {
  return updateStaffMember(userId, { isActive: true });
};

// Delete staff member (permanent)
export const deleteStaffMember = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
  
      const userData = userDoc.data();
      
      // Delete profile picture from storage if it exists
      if (userData.photoURL) {
        try {
          const photoRef = ref(storage, userData.photoURL);
          await deleteObject(photoRef);
        } catch (storageError) {
          console.warn('Error deleting profile picture:', storageError);
          // Continue with user deletion even if photo deletion fails
        }
      }
  
      // Soft delete in Firestore
      await updateDoc(userRef, {
        email: deleteField(),
        isActive: false,
        deletedAt: new Date().toISOString()
      });
  
      // Note: Authentication deletion should be handled via a Cloud Function
      // as client-side user deletion requires recent login
      // The Cloud Function should be triggered by the document update above
      // and handle the actual auth user deletion
  
      return { success: true };
    } catch (error) {
      console.error('Error deleting staff member:', error);
      throw error;
    }
  };

// Send password reset email
export const resetStaffPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

// Get all staff members for the current company
export const getStaffMembers = async (companyId) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('companyId', '==', companyId),
      where('role', 'in', ['staff', 'admin', 'manager'])
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting staff members:', error);
    throw error;
  }
};
